import React, { useEffect, useMemo, useState } from 'react';
import type { ParagraphBlock, LegalRun } from '../models/ast';
import { layoutParagraph } from '../engine/ParagraphLayout';
import { getStylesRegistry, setStylesRegistry } from '../styles/registry';

type TestResult = { name: string; passed: boolean; details?: string };

function assert(cond: boolean, msg: string): TestResult {
  return { name: msg, passed: !!cond, details: cond ? undefined : msg };
}

function summarize(results: TestResult[]): { pass: number; fail: number } {
  let pass = 0, fail = 0;
  results.forEach(r => (r.passed ? pass++ : fail++));
  return { pass, fail };
}

export default function Sprint2LayoutRunner(): React.JSX.Element {
  const [results, setResults] = useState<TestResult[]>([]);
  const [running, setRunning] = useState(false);

  const runAll = useMemo(() => async (): Promise<void> => {
    setRunning(true);
    const out: TestResult[] = [];

    // Ensure a minimal registry exists
    const base = getStylesRegistry();
    const next = {
      characterStyles: { ...(base.characterStyles || {}) },
      paragraphStyles: { ...(base.paragraphStyles || {}) },
    } as any;
    next.characterStyles['t_char'] = { id: 't_char', fontFamily: 'Inter', fontSizePt: 12, fontWeight: 400 };
    next.characterStyles['t_char_bold'] = { id: 't_char_bold', fontFamily: 'Inter', fontSizePt: 12, fontWeight: 700 };
    next.paragraphStyles['t_para'] = { id: 't_para', lineSpacingMultiple: 1.5, defaultCharacterStyleId: 't_char' };
    setStylesRegistry(next);

    // Helper to typeset a paragraph
    async function typeset(textRuns: LegalRun[], width: number) {
      const block: ParagraphBlock = { id: 'p', type: 'Paragraph', attrs: {}, children: textRuns, styleId: 't_para' } as any;
      return layoutParagraph(block, { widthPx: width });
    }

    try {
      // T1: single-run at two widths, baselines strictly increasing
      {
        const tRuns: LegalRun[] = [{ id: 'r1', text: 'The quick brown fox jumps over the lazy dog.', marks: {} }];
        for (const w of [280, 460]) {
          const res = await typeset(tRuns, w);
          const ok = res.lineBoxes.every((b, i, a) => i === 0 || b.baseline > a[i - 1].baseline);
          out.push(assert(ok, `T1 baselines strictly increasing @${w}px`));
        }
      }

      // T2: multi-run (bold second half), x monotonic within a line
      {
        const runs: LegalRun[] = [
          { id: 'r2a', text: 'The quick brown ', marks: {} },
          { id: 'r2b', text: 'fox jumps over the lazy dog.', marks: {}, charStyleId: 't_char_bold' },
        ];
        const res = await typeset(runs, 420);
        const byLine: Record<number, number[]> = {};
        Object.values(res.graphemeMap).forEach(({ lineIndex, x }) => {
          const arr = (byLine[lineIndex] ||= []);
          arr.push(x);
        });
        const ok = Object.values(byLine).every(arr => arr.every((v, i) => i === 0 || v >= arr[i - 1]));
        out.push(assert(ok, 'T2 monotonic x within line (multi-run)'));
      }

      // T3: zero-width inline anchor (simulate footnote anchor)
      {
        const runs: LegalRun[] = [
          { id: 'r3a', text: 'Hello', marks: {} },
          { id: 'r3z', text: '', marks: {} }, // anchor
          { id: 'r3b', text: ' world', marks: {} },
        ];
        const res = await typeset(runs, 300);
        const anchor = res.graphemeMap['r3z:0'];
        const ok = !!anchor && anchor.lineIndex >= 0;
        out.push(assert(ok, 'T3 zero-width anchor mapped to a line'));
      }

      // T4: empty paragraph → no lines
      {
        const runs: LegalRun[] = [];
        const res = await typeset(runs, 300);
        const ok = res.lineBoxes.length === 0 && res.heightPx === 0;
        out.push(assert(ok, 'T4 empty paragraph yields no lines'));
      }

      // T5: grapheme coverage contiguous across lines
      {
        const runs: LegalRun[] = [{ id: 'r5', text: 'A fairly long sentence that will wrap across multiple lines for coverage.', marks: {} }];
        const res = await typeset(runs, 320);
        let ok = true;
        for (let i = 0; i < res.lineBoxes.length - 1; i++) {
          const a = res.lineBoxes[i];
          const b = res.lineBoxes[i + 1];
          if (b.startGrapheme !== a.endGrapheme + 1) { ok = false; break; }
        }
        out.push(assert(ok, 'T5 grapheme coverage contiguous across lines'));
      }

    } catch (err: any) {
      out.push({ name: 'Harness error', passed: false, details: err?.message || String(err) });
    }

    setResults(out);
    setRunning(false);
  }, []);

  useEffect(() => { runAll(); }, [runAll]);

  const { pass, fail } = summarize(results);

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ margin: '8px 0' }}>EdgeDocs Sprint 2 Layout Runner</h2>
      <div style={{ marginBottom: 12, color: fail ? '#dc2626' : '#16a34a' }}>
        {running ? 'Running…' : `Summary: PASS=${pass} FAIL=${fail}`}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: 6 }}>Check</th>
            <th style={{ textAlign: 'left', padding: 6 }}>Result</th>
            <th style={{ textAlign: 'left', padding: 6 }}>Details</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r, idx) => (
            <tr key={idx}>
              <td style={{ padding: 6 }}>{r.name}</td>
              <td style={{ padding: 6, color: r.passed ? '#16a34a' : '#dc2626' }}>{r.passed ? 'PASS' : 'FAIL'}</td>
              <td style={{ padding: 6 }}>{r.details || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}










































